<script setup lang="ts">
import type { SectionStatuses, UseFinancingData } from '~/store-v2/financings/useFinancing'
import type { RegistrationUpdateByBiometryForm } from '~/store-v2/registration/useRegistrationContactChange'
import type { StageFormalization, TagStep } from '~/ui-blocks/Formalization/Formalization.types'
import type { BiometryAlertStatus } from '~/utils/customer-register/Biometry'
import { SolAlert, SolButton, SolDivider, useToast } from '@solfacil/girassol'
import IconLock from '~icons/material-symbols/lock'
import IconOpenInNew from '~icons/material-symbols/open-in-new'
import Loading from '~/components/loading/index.vue'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import { provideFormalizationReadOnly } from '~/composables/financing/useFormalizationReadOnly'
import { useTerminalStatusLock } from '~/composables/financing/useTerminalStatusLock'
import { useDateProps } from '~/composables/proposal/useDateProps'
import { useFormalizationStatusReasonAlert } from '~/composables/useFormalizationStatusReasonAlert'
import { STATUSES_MAPPING } from '~/services-v2/financial-bff/financial-bff.consts'
import useCustomerBiometry from '~/store-v2/contract-formalization/useCustomerBiometry'
import useCustomerBiometryCancel from '~/store-v2/contract-formalization/useCustomerBiometryCancel'
import useCustomerBiometryResend from '~/store-v2/contract-formalization/useCustomerBiometryResend'
import useCustomerBiometrySend from '~/store-v2/contract-formalization/useCustomerBiometrySend'
import useFinancing from '~/store-v2/financings/useFinancing'
import { FinancingStatus } from '~/store-v2/financings/useFinancings'
import useProject from '~/store-v2/projects/useProject'
import useRegistration from '~/store-v2/registration/useRegistration'
import { useRegistrationContactChange } from '~/store-v2/registration/useRegistrationContactChange'
import { useEquipmentEditStore } from '~/store/equipmentEdit'
import { Formalization } from '~/types/enum'
import { getStoreOrderErrorMessageKey, parseSaveError } from '~/ui-blocks/Equipment/storeOrderErrors'
import { GeneralStatuses } from '~/utils/financing-flow/Financing'
import BiometryConfirmationCards from './BiometryConfirmationCards.vue'
import BiometryTimeline from './BiometryTimeline.vue'
import DocumentsModal from './DocumentsModal.vue'

const props = defineProps<{
  position: number
  statusProject: SectionStatuses
  stage: StageFormalization
  stageReproved: string
}>()

const emit = defineEmits<{
  (e: 'statusBiometry', v: UseFinancingData): void
  (e: 'alertBiometry', v: BiometryAlertStatus): void
}>()

const route = useRoute<any>()
const router = useRouter()
const projectId = computed(() => String(route.params.id))

const { t } = useI18n()
const { createErrorToast } = useToast()
const toast = useToast()

// constants
const helpcenterUrl = 'https://helpcentersolfacil.zendesk.com/hc/pt-br/articles/15977819037467-Como-cancelo-meu-financiamento'

// states
const status = ref({} as UseFinancingData)
const showModalCancel = ref(false)
const enablePooling = ref(false)
const loadingMessages = t('biometry.loading.messages').split(', ')
const previewDocument = ref(false)
const showEquipmentEditAlert = ref(false)

const equipmentEditStore = useEquipmentEditStore()
const isEditingEquipment = computed(() => equipmentEditStore.isProjectBeingEdited(projectId.value))

const selectedOrderId = ref<string>('')
const editContactOpen = ref(false)
const isCancelInFlight = ref(false)
const orderError = ref('')

function resolveComboOrderError(backendMessage?: string): string {
  const message = (backendMessage ?? '').toLowerCase()
  if (message.includes('compat'))
    return t('customer.biometry.compatibility.errors.incompatible')
  if (message.includes('expir'))
    return t('customer.biometry.compatibility.errors.expired')
  return t('customer.biometry.compatibility.errors.generic')
}

const {
  data: financing,
  isLoading: isLoadingFinancing,
} = useFinancing(projectId)

const { isTerminalLocked, isTerminalReadOnly, terminalTag } = useTerminalStatusLock(financing, 'biometry_and_contract', t)

// Shares the terminal read-only flag with nested components (e.g. ComboConecteModal)
provideFormalizationReadOnly(isTerminalReadOnly)

const {
  data: project,
  isLoading: isLoadingProject,
} = useProject(projectId)

const {
  data: registration,
  isLoading: isLoadingRegistration,
} = useRegistration(projectId)

const {
  mutateAsync: updateRegistrationContactChange,
  isPending: isUpdateCustomerPending,
} = useRegistrationContactChange(projectId.value)

const {
  data: customerBiometrics,
  isError: isErrorCustomerBiometrics,
  isLoading: isLoadingCustomerBiometrics,
  refetch: refetchCustomerBiometrics,
  isFetching: isFetchingCustomerBiometrics,
} = useCustomerBiometry(projectId, enablePooling)

const { statusReasonAlert } = useFormalizationStatusReasonAlert(customerBiometrics)

function hasSignedDocuments(data: any): boolean {
  if (!data?.contracts) {
    return false
  }

  const contracts = data.contracts

  if (contracts.loan?.status === 'signed' && contracts.loan?.document_signed) {
    return true
  }

  if (contracts.combo_connect_term?.status === 'signed' && contracts.combo_connect_term?.document_signed) {
    return true
  }

  if (contracts.lender_insurance?.status === 'signed' && contracts.lender_insurance?.document_signed) {
    return true
  }

  if (Array.isArray(contracts.financing_amendment_term)) {
    const hasSignedTAF = contracts.financing_amendment_term.some(
      (item: any) => item?.status === 'signed' && item?.document_signed,
    )
    if (hasSignedTAF) {
      return true
    }
  }

  return false
}

const hasAvailableDocuments = computed(() => hasSignedDocuments(customerBiometrics.value))

const isComboConnect = computed(() => Boolean(project.value?.selected_installments?.is_combo_connect))

watch(customerBiometrics, (data) => {
  if (data?.order_id)
    selectedOrderId.value = String(data.order_id)
}, { immediate: true })

watch(previewDocument, (isOpen) => {
  if (isOpen) {
    enablePooling.value = false
  }
  else {
    enablePooling.value = true
  }
})

const {
  mutateAsync: mutateCustomerBiometrySend,
  isPending: isPendingSend,
} = useCustomerBiometrySend(projectId)

const {
  mutateAsync: mutateCustomerBiometryResend,
  isPending: isPendingResend,
} = useCustomerBiometryResend(projectId)

const {
  mutateAsync: mutateCustomerBiometryCancel,
  isPending: isPendingCancel,
} = useCustomerBiometryCancel(projectId)

const isApprovedReprovedCanceledOrExpired = computed(() => [
  GeneralStatuses.APPROVED,
  GeneralStatuses.REPROVED,
  GeneralStatuses.CANCELED,
  GeneralStatuses.EXPIRED,
].includes(financing.value?.section_statuses?.biometry_and_contract?.status as GeneralStatuses))

const isCanceledOrExpired = computed(() => [
  GeneralStatuses.CANCELED,
  GeneralStatuses.EXPIRED,
].includes(financing.value?.section_statuses?.biometry_and_contract?.status as GeneralStatuses))

const isApproved = computed(() => financing.value?.section_statuses?.biometry_and_contract?.status === GeneralStatuses.APPROVED)

const canInteractWithProjectVerification = computed(() => [
  GeneralStatuses.IN_PROGRESS,
  GeneralStatuses.TO_REVIEW,
].includes(financing.value?.section_statuses?.biometry_and_contract?.status as GeneralStatuses))

const isBiometryReproved = computed(() =>
  financing.value?.section_statuses?.biometry_and_contract?.status === GeneralStatuses.REPROVED,
)

const showSendButton = computed(() => {
  if (!customerBiometrics.value || !financing.value)
    return false
  return ['waiting_to_start', 'in_progress'].includes(customerBiometrics.value.status)
})

const projectActionsDisabled = computed(() =>
  !canInteractWithProjectVerification.value
  || isTerminalReadOnly.value,
)

const projectVerificationDisabled = computed(() =>
  projectActionsDisabled.value
  || !showSendButton.value,
)

const showCancelButton = computed(() => {
  if (!customerBiometrics.value || !financing.value)
    return false

  return customerBiometrics.value.cancel === true
})

const projectEditDisabled = computed(() =>
  isBiometryReproved.value
  || isTerminalReadOnly.value,
)

const showResendButton = computed(() => {
  if (!customerBiometrics.value || !financing.value)
    return false
  return (financing.value?.section_statuses?.biometry_and_contract?.status === GeneralStatuses.REPROVED)
    || customerBiometrics.value.biometry.status === 'in_progress'
})

const isLoading = computed(() =>
  isLoadingRegistration.value
  || isLoadingFinancing.value
  || isLoadingProject.value
  || isLoadingCustomerBiometrics.value,
)

const openCollapse = computed(() => {
  return props.stage === Formalization.BIOMETRY_AND_CONTRACT
    && !!financing.value?.openable_steps?.contract
})

const { hasPendingTAF } = usePendingUpdateEquipment(projectId)

const tag = computed(() => {
  if (hasPendingTAF.value) {
    return {
      id: 'waiting_customer',
      text: 'Aguardando cliente',
      variant: 'fill',
      size: 'medium',
      type: 'warning',
    } as TagStep
  }

  const sectionStatus = financing.value?.section_statuses?.biometry_and_contract?.status
  const isOpenableStep = financing.value?.openable_steps?.contract

  if (!sectionStatus || !isOpenableStep)
    return

  const mappedStatus = STATUSES_MAPPING[sectionStatus]

  if (!mappedStatus)
    return

  return {
    id: sectionStatus,
    text: t(mappedStatus.textKey),
    variant: 'fill',
    size: 'medium',
    type: mappedStatus.color,
  } as TagStep
})

const alert = computed(() => {
  const alert = customerBiometrics.value?.pendencies?.items[0]

  if (!alert) {
    return null
  }

  const dic: Record<string, { title: string, text: string, feedback: 'warning' | 'informative' }> = {
    under_analysis: {
      title: alert.message,
      text: t('customer.biometry.alert.under_analysis.text'),
      feedback: 'warning',
    },
    pending: {
      title: t('customer.biometry.alert.pending.title'),
      text: t('customer.biometry.alert.pending.text'),
      feedback: 'informative',
    },
    blocklist: {
      title: t('customer.biometry.alert.blocklist.title'),
      text: alert.message,
      feedback: 'warning',
    },
  } as const

  return dic[alert.category]
})

onMounted(async () => {
  await refetchCustomerBiometrics()
})

async function handleSendBiometry() {
  if (isTerminalReadOnly.value || projectVerificationDisabled.value || isPendingSend.value)
    return

  orderError.value = ''

  try {
    const sendBiometryResponse = await mutateCustomerBiometrySend(
      isComboConnect.value ? (selectedOrderId.value || undefined) : undefined,
    )

    if (sendBiometryResponse) {
      toast.createSuccessToast({
        description: t('customer.biometry.formalization_started'),
      })
    }
    else {
      handleBiometrySendError()
    }
  }
  catch (error: any) {
    console.error(error)

    const { reasonCode, message } = parseSaveError(error)
    const storeOrderMessageKey = getStoreOrderErrorMessageKey(reasonCode)

    if (storeOrderMessageKey) {
      const description = t(storeOrderMessageKey)
      orderError.value = description
      toast.createErrorToast({ description })
      return
    }

    if (error?.response?.status === 422) {
      const backendMessage = typeof message === 'string' && message
        ? message
        : (error?.response?.data?.detail ?? error?.response?.data?.message)
      orderError.value = resolveComboOrderError(
        typeof backendMessage === 'string' ? backendMessage : message,
      )
      return
    }

    if (message) {
      toast.createErrorToast({ description: message })
      return
    }

    handleBiometrySendError()
  }
}

function handleBiometrySendError() {
  toast.createErrorToast({
    description: t('customer.biometry.catch.send_error'),
  })
}

async function handleBiometryContactUpdate(payload: RegistrationUpdateByBiometryForm) {
  if (isTerminalReadOnly.value || projectVerificationDisabled.value || isUpdateCustomerPending.value)
    return

  try {
    await updateRegistrationContactChange(payload)

    editContactOpen.value = false

    toast.createSuccessToast({
      description: t('customer.biometry.update_success'),
    })
  }
  catch (error: unknown) {
    const errorMessage = (error as { message?: string })?.message

    if (errorMessage === 'NO_RESPONSE') {
      toast.createErrorToast({
        title: t('toast.error.send.title'),
        description: t('toast.error.edit.biometry.send.description'),
      })
    }
    else {
      const descriptionKey = `customer.biometry.catch.know_errors.${errorMessage}`
      const fallbackDescription = t('customer.biometry.catch.update_error')

      toast.createErrorToast({
        description: t(descriptionKey, fallbackDescription),
      })
    }
  }
}

async function handleResend() {
  if (isTerminalReadOnly.value || isBiometryReproved.value || isPendingResend.value)
    return

  try {
    await mutateCustomerBiometryResend()
  }
  catch (error) {
    console.error(error)
    createErrorToast(t('customer.biometry.catch.resend_error'))
  }
}

async function handleCancel() {
  if (isTerminalReadOnly.value || projectEditDisabled.value || isPendingCancel.value || isCancelInFlight.value)
    return

  showModalCancel.value = false
  isCancelInFlight.value = true

  try {
    const responseCancelBiometry = await mutateCustomerBiometryCancel()
    const STATUS_CODE_204_NO_CONTENT = Object.keys(responseCancelBiometry).length === 0

    if (STATUS_CODE_204_NO_CONTENT) {
      if (status.value?.section_statuses?.biometry_and_contract) {
        status.value.section_statuses.biometry_and_contract.status = GeneralStatuses.IN_PROGRESS

        emit('statusBiometry', status.value)

        toast.createSuccessToast({
          description: t('customer.biometry.cancel_success'),
        })
      }
      await refetchCustomerBiometrics()
    }
    else {
      createErrorToast(t('customer.biometry.catch.cancel_error'))
    }
  }
  catch (error: any) {
    const partialMessageHasBeenCancelled = 'Contract has been cancelled, wait'
    const minutes = error?.response?.data?.detail.match(/\d+/)[0]

    const errorMessage = error?.response?.data?.detail?.includes(partialMessageHasBeenCancelled)
      ? t('customer.biometry.catch.has_been_cancelled', { minutes })
      : t('customer.biometry.catch.cancel_error')

    createErrorToast(errorMessage)
  }
  finally {
    isCancelInFlight.value = false
  }
}

function handleGoToDueDate() {
  if (isTerminalReadOnly.value || projectVerificationDisabled.value)
    return

  router.push(`/simulation/details/${projectId.value}?edit`)
}

function handleGoToStore() {
  if (isTerminalReadOnly.value || projectVerificationDisabled.value)
    return

  const storeUrl = import.meta.env.MODE === 'production'
    ? 'https://loja.solfacil.com.br/'
    : 'https://stg-loja.solfacil.com.br/'
  window.open(storeUrl, '_blank')
}

function handleSend() {
  if (isTerminalReadOnly.value || projectVerificationDisabled.value || isPendingSend.value || (isComboConnect.value && !selectedOrderId.value))
    return

  if (isEditingEquipment.value) {
    showEquipmentEditAlert.value = true
    return
  }

  handleSendBiometry()
}

function handleOrderIdUpdate(value: string) {
  if (isTerminalReadOnly.value || projectVerificationDisabled.value)
    return

  selectedOrderId.value = value
  orderError.value = ''
}

function handleOpenCancelModal() {
  if (isTerminalReadOnly.value || projectEditDisabled.value || isPendingCancel.value || isCancelInFlight.value)
    return

  showModalCancel.value = true
}

watch(projectVerificationDisabled, (disabled) => {
  if (disabled)
    editContactOpen.value = false
})

watch(projectEditDisabled, (disabled) => {
  if (disabled)
    showModalCancel.value = false
})

const statusProject = computed(() => props.statusProject)

watch(statusProject, () => {
  enablePooling.value = props.statusProject?.equipment?.status === 'approved'
    && props.statusProject?.customer_data?.status === 'approved'
}, { immediate: true })

const submittedAt = computed(() => financing.value?.section_statuses?.biometry_and_contract?.submitted_at)
const completedAt = computed(() => financing.value?.section_statuses?.biometry_and_contract?.completed_at)
const reprovedAt = computed(() => financing.value?.section_statuses?.biometry_and_contract?.reproved_at)

const dateProps = useDateProps('contract', {
  completedAt,
  reprovedAt,
  submittedAt,
  financing,
})

const isApprovedPaymentModel = computed(() => financing.value?.section_statuses?.payment_model?.status === 'approved')

const isApprovedOrReprovedOrExpiredOrCanceledStatus = computed(() => {
  return [
    FinancingStatus.approved,
    FinancingStatus.reproved,
    FinancingStatus.expired,
    FinancingStatus.canceled,
  ].includes(financing.value?.status as FinancingStatus)
})

const isApprovedStepStatus = computed(() => {
  return financing.value?.section_statuses?.biometry_and_contract?.status === 'approved'
})

const hasShownStatus = computed(() => isApprovedPaymentModel.value || isApprovedOrReprovedOrExpiredOrCanceledStatus.value || isApprovedStepStatus.value)
const showDateProps = computed(() => submittedAt.value || completedAt.value || reprovedAt.value || !hasShownStatus.value)
const documentsModal = ref(false)

function openDocumentsModal() {
  documentsModal.value = true
}

function closeDocumentsModal() {
  documentsModal.value = false
}
</script>

<template class="customer-biometry">
  <CustomerElementAccordion
    :id="`accordion_${Formalization.BIOMETRY_AND_CONTRACT}`"
    :title="t('customer.biometry.title')"
    :position="String(position)"
    :tag="terminalTag ?? tag"
    :blocked="isTerminalLocked || !financing?.openable_steps?.contract"
    :open-collapse="openCollapse && !isTerminalLocked"
    :has-shown-status="hasShownStatus"
    v-bind="showDateProps && !hasPendingTAF ? dateProps : null"
    class="pb-6"
  >
    <template #icon>
      <IconLock v-if="isTerminalLocked || !financing?.openable_steps?.contract" />
    </template>

    <template v-if="customerBiometrics && financing?.openable_steps?.contract">
      <div class="mt-2">
        <p>{{ t('customer.biometry.step_subtitle') }}</p>
      </div>

      <SolAlert
        v-if="statusReasonAlert"
        id="alert-formalization-status-reason"
        class="mt-4"
        :title="statusReasonAlert.title"
        :feedback="statusReasonAlert.feedback"
      >
        {{ statusReasonAlert.description }}
      </SolAlert>

      <BiometryConfirmationCards
        v-model:edit-open="editContactOpen"
        class="mt-6"
        :registration="registration"
        :project="project"
        :project-id="projectId"
        :is-update-customer-pending="isUpdateCustomerPending"
        :is-combo-connect="isComboConnect"
        :order-id="selectedOrderId"
        :confirmed="!showSendButton"
        :disabled="projectVerificationDisabled"
        :show-confirm="true"
        :confirm-loading="isPendingSend"
        :confirm-disabled="projectVerificationDisabled || (isComboConnect && !selectedOrderId)"
        :show-edit-project="showCancelButton"
        :edit-project-loading="isPendingCancel || isCancelInFlight"
        :edit-project-disabled="projectEditDisabled"
        :store-disabled="projectVerificationDisabled"
        :order-error="orderError"
        @submit-edit="handleBiometryContactUpdate"
        @go-to-due-date="handleGoToDueDate"
        @update:order-id="handleOrderIdUpdate"
        @go-to-store="handleGoToStore"
        @confirm="handleSend"
        @edit-project="handleOpenCancelModal"
      />

      <SolAlert
        v-if="alert"
        id="alert-biometry"
        :title="alert.title"
        :text="alert.text"
        :feedback="alert.feedback" class="mt-7"
      />

      <div class="mt-6 p-4 md:system:p-6 rounded-lg border border-neutral-high-medium">
        <div class="flex flex-col gap-1 mb-6">
          <span class="fonts-body-large-bold text-neutral-low-dark">{{ t('customer.biometry.steps_box.title') }}</span>
          <span class="fonts-body-medium-regular text-neutral-low-medium">{{ t('customer.biometry.steps_box.description') }}</span>
        </div>

        <BiometryTimeline
          :data="customerBiometrics"
          :biometry-stage-status="financing.section_statuses?.biometry_and_contract?.status"
          :show-date-props="Boolean(showDateProps)"
          :disabled="isBiometryReproved || isCanceledOrExpired || isTerminalReadOnly"
          :read-only="isTerminalReadOnly"
        />
      </div>

      <SolDivider thickness="x-small" orientation="horizontal" class="my-7" />

      <div
        class="flex flex-col md:system:flex-row flex-col-reverse justify-between"
        :class="{ 'pointer-events-none opacity-60': isCanceledOrExpired }"
      >
        <a :href="helpcenterUrl" target="_blank" class="flex items-center gap-2 text-brand-secondary-dark">
          <span>{{ t('customer.biometry.footer.policy') }}</span>
          <IconOpenInNew />
        </a>

        <div class="flex flex-col md:system:flex-row  flex-col-reverse justify-end gap-3 mb-7 md:system:mb-0">
          <SolButton
            v-if="showResendButton" id="customer_resend" size="large" variant="secondary"
            class="w-full hidden" :loading="isPendingResend"
            :disabled="isApprovedReprovedCanceledOrExpired || isTerminalReadOnly" @click="handleResend"
          >
            {{ t('customer.biometry.footer.resend') }}
          </SolButton>

          <SolButton
            v-if="isApproved && hasAvailableDocuments"
            id="preview_contract"
            size="large"
            class="w-full"
            :disabled="isCanceledOrExpired"
            @click="openDocumentsModal()"
          >
            <div class="flex items-center gap-3 whitespace-nowrap">
              <span>{{ t('customer.biometry.footer.contract') }}</span>
            </div>
          </SolButton>
        </div>
      </div>
    </template>
    <template v-else>
      <template v-if="isLoading || isFetchingCustomerBiometrics">
        <div class="container">
          <div class="my-4 card-container w-full h-96 flex justify-center">
            <Loading :messages="loadingMessages" />
          </div>
        </div>
      </template>

      <template v-if="isErrorCustomerBiometrics">
        <div class="w-full flex justify-center">
          <SolEmptyState
            id="empty-state"
            :subtitle="t('customer.biometry.catch.data_error')"
            :title="t('customer.biometry.catch.data_error_title')"
            variant="no-content"
          >
            <template #action>
              <SolButton
                id="retry-biometry"
                @click="refetchCustomerBiometrics"
              >
                {{ t('biometry.button.try_again') }}
              </SolButton>
            </template>
          </SolEmptyState>
        </div>
      </template>
    </template>
  </CustomerElementAccordion>

  <SolModal
    id="modal" :is-open="showModalCancel" :title="t('customer.biometry.modal_cancel.title')"
    :action-primary-text="t('customer.biometry.modal_cancel.confirm')"
    :action-secondary-text="t('customer.biometry.modal_cancel.cancel')"
    :size="{ desktop: 'medium', mobile: 'bottom-sheet' }" @close="showModalCancel = false"
    @action:primary="handleCancel" @action:secondary="showModalCancel = false"
  >
    <div>{{ t('customer.biometry.modal_cancel.description') }}</div>
  </SolModal>

  <SolModal
    id="equipment-edit-alert-modal"
    :is-open="showEquipmentEditAlert"
    :title="t('customer.equipment_update.alert.pending_edit_title')"
    action-primary-text="Entendi"
    :size="{ desktop: 'medium', mobile: 'bottom-sheet' }"
    @close="showEquipmentEditAlert = false"
    @action:primary="showEquipmentEditAlert = false"
  >
    <p>{{ t('customer.equipment_update.alert.pending_edit_description') }}</p>
  </SolModal>

  <DocumentsModal
    :is-open="documentsModal"
    :customer-biometrics="customerBiometrics"
    @close="closeDocumentsModal"
  />
</template>

<style lang="scss" scoped>
.customer-biometry {
  :deep(.sol-overlay-core) {
    @apply z-10;
  }
}

.preview-iframe {
  @apply border-none w-full h-[85dvh];
}
</style>
